<template>
    <div>
        <div class="min-h-screen flex relative lg:static surface-ground">
            <!-- app-sidebar -->
            <div
                id="app-sidebar"
                :class="{'block': sidebarShow}"
                class="app-sidebar surface-section h-full lg:h-auto hidden lg:block flex-shrink-0 absolute lg:static left-0 top-0 z-1 border-right-1 surface-border select-none"
                style="width: 280px;">
                <!-- flex-column -->
                <div class="h-full bg-gray-100">
                    <div class="flex justify-content-end align-items-center">
                        <a @click="toggleSidebar" class="cursor-pointer block lg:hidden text-700 mr-3 mt-1 p-ripple" data-pd-ripple="true" data-pd-styleclass="true">
                            <i class="pi pi-times"></i>
                        </a>
                    </div>
                    <div class="text-center p-1 lg:p-3 font-semibold text-2xl text-primary">
                        <img class="app-logo" src="/public/logo_gts.png" alt="GTS" width="175"/>
                    </div>
                    <!-- MENU SKELETON -->
                    <Tree
                        v-if ="isLoading"
                        :value="skeleton"
                        :filter="true"
                        class="main-menu w-full bg-gray-100">

                        <template #nodeicon="{node}">
                            <Skeleton size="1rem"></Skeleton>
                        </template>
                        <template #default="{node}">
                            <div class="flex align-items-center gap-2">
                                <Skeleton width="11rem"></Skeleton>
                            </div>
                        </template>
                    </Tree>

                    <!-- MENU CARREGADO -->
                    <Tree
                        v-else
                        :value="sidebar"
                        v-model:expandedKeys="expandedKeys"
                        :filter="true"
                        scrollHeight="calc(70vh)"
                        class="main-menu w-full bg-gray-100">
                        <template #default="{node}">
                            <template v-if="node.para">
                                <RouterLink class="main-menu-item" :to="{name: node.para}">{{ node.label }}</RouterLink>
                            </template>
                            <template v-else>
                                {{ node.label }}
                            </template>
                        </template>
                    </Tree>
                </div>
                <!-- flex-column -->
            </div>
            <!-- app-sidebar -->

            <!-- mobile sidebar backdrop -->
            <div
                v-if="sidebarShow"
                class="mobile-sidebar-backdrop lg:hidden"
                @click="toggleSidebar">
            </div>

            <!-- app-body -->
            <div class="min-h-screen flex flex-column relative flex-auto">
                <!-- top-bar -->
                <div class="app-topbar flex justify-content-between align-items-center px-5 border-bottom-1 relative lg:static bg-gray-600 text-gray-100" style="height: 60px;">
                    <div class="flex text-gray-100">
                        <a @click="toggleSidebar" class="cursor-pointer block lg:hidden text-700 mr-3 mt-1 p-ripple text-gray-100" data-pd-ripple="true" data-pd-styleclass="true">
                            <i class="pi pi-bars text-4xl"></i>
                            <span role="presentation" aria-hidden="true" data-p-ink="true" data-p-ink-active="false" class="p-ink" data-pc-name="ripple" data-pc-section="root"></span>
                        </a>
                    </div>

                    <ul class="list-none hidden lg:flex my-0 align-items-center gap-2">
                        <li>
                            <Button
                                :icon="isDarkMode ? 'pi pi-sun' : 'pi pi-moon'"
                                :label="isDarkMode ? 'Light' : 'Dark'"
                                size="small"
                                class="darkmode-toggle-btn"
                                @click="toggleDarkMode"
                            />
                        </li>
                        <li v-if="usuario.id" class="border-top-1 surface-border lg:border-top-none font-semibold ">
                            <a @click="toggleMenu" class="flex p-3 lg:px-3 lg:py-2 align-items-center hover:text-900 hover:surface-100 font-small border-round cursor-pointer p-ripple" data-pd-ripple="true">
                                <span class="mr-2">{{ usuario.nome }}</span>
                                <i class="pi pi-user text-base lg:text-xl mr-2 lg:mr-0 p-overlay-badge" data-pd-badge="true" data-p-overlay-badge="true"></i>
                            </a>
                        </li>
                    </ul>

                    <div class="mobile-top-actions lg:hidden">
                        <Button
                            :icon="isDarkMode ? 'pi pi-sun' : 'pi pi-moon'"
                            class="mobile-theme-btn"
                            size="small"
                            @click="toggleDarkMode"
                        />
                        <a class="cursor-pointer block text-700 p-ripple text-gray-100" data-pd-ripple="true" data-pd-styleclass="true" @click="toggleMenu">
                            <i class="pi pi-ellipsis-v text-2xl"></i>
                            <span role="presentation" aria-hidden="true" data-p-ink="true" data-p-ink-active="false" class="p-ink" data-pc-name="ripple" data-pc-section="root"></span>
                        </a>
                    </div>
                    <TieredMenu ref="menu" id="overlay_tmenu" :model="items" popup />
                </div>
                <!-- top-bar -->

                <!-- content -->
                <div class="app-content flex flex-column flex-auto p-2">
                    <router-view :key="$route.path + '-theme-' + chartRefreshKey"></router-view>
                </div>
                <!-- content -->

                <div class="app-footer mt-auto p-2 text-right bg-gray-800 text-gray-400 text-xs">
                    criado e mantido por <span class="underline">George Rotteny</span> &lt;grotteny@hotmail.com&gt;. todos os direitos reservados.
                </div>
            </div>
            <!-- app-body -->
        </div>

        <ProfileForm :visible="visibleProfileForm" :data="usuario" @close="visibleProfileForm=false" @save="onSaveProfileForm" />
        <PasswordForm :visible="visiblePasswordForm" :data="usuario" @close="visiblePasswordForm=false" />
    </div>
</template>

<script>
import Button from 'primevue/button';
import Tree from 'primevue/tree';
import TieredMenu from 'primevue/tieredmenu';
import Drawer from 'primevue/drawer';
import Skeleton from 'primevue/skeleton';

import ProfileForm from '@/components/auth/ProfileForm.vue';
import PasswordForm from '@/components/auth/PasswordForm.vue';

import Auth from '@/service/auth.js';
import Menu from '@/service/menu.js';

import Token from '@/utils/Token.js';

export default {
    components: {
        Button,
        Drawer,
        Tree,
        Skeleton,
        TieredMenu,
        ProfileForm,
        PasswordForm,
    },
    data() {
        return {
            isLoading: false,
            isDarkMode: false,
            chartRefreshKey: 0,
            visibleProfileForm: false,
            visiblePasswordForm: false,
            usuario: {},
            sidebarShow: false,
            expandedKeys: {},
            items: [
                {
                    label: 'Perfil',
                    icon: 'pi pi-user',
                    command: this.perfil
                },
                {
                    label: 'Senha',
                    icon: 'pi pi-lock',
                    command: this.senha
                },
                {
                    separator: true
                },
                {
                    label: 'Sair',
                    icon: 'pi pi-sign-out',
                    command: this.logout
                },
            ],
            skeleton: [
                {},{},{},
            ],
            sidebar: [],
        };
    },
    beforeMount() {
        this.loadThemePreference();

        this.me();

        this.isLoading = true;
        Menu.side()
                .then(response => {
                    this.sidebar = response.data;

                    this.expandedKeys = {};
                    this.expandAll(this.sidebar);
                })
                .catch(error => {
                    if(error.response) {
                        if(error.response.data.msg) {
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                            return;
                        }
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
    },
    methods: {
        onSaveProfileForm(usuario) {
            this.usuario = usuario;
        },
        loadThemePreference() {
            this.isDarkMode = localStorage.getItem('gts_theme_dark') === '1';
            this.applyThemeClass();
        },
        applyThemeClass() {
            document.documentElement.classList.toggle('my-app-dark', this.isDarkMode);
            window.dispatchEvent(new Event('theme-changed'));
        },
        toggleDarkMode() {
            this.isDarkMode = !this.isDarkMode;
            localStorage.setItem('gts_theme_dark', this.isDarkMode ? '1' : '0');
            this.applyThemeClass();
            this.chartRefreshKey += 1;
        },
        me() {
            Auth.me()
                .then(response => {
                        this.usuario = response.data;
                    })
                    .catch(error => {
                        if(error.response) {
                            if(error.response.data.msg) {
                                this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                                return;
                            }
                        }
                        this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                    });
        },
        toggleSidebar() {
            this.sidebarShow = !this.sidebarShow;
        },
        toggleMenu(event) {
            this.$refs.menu.toggle(event);
        },
        expandAll(lista) {
            lista.forEach(item => {
                this.expandedKeys[item.key] = true;
                if(item.children) {
                    this.expandAll(item.children);
                }
            });
        },
        perfil() {
            this.visibleProfileForm=true;
        },
        senha() {
            this.visiblePasswordForm=true;
        },
        logout() {
            this.$confirm.require({
                message: 'Deseja realmente sair do sistema?',
                header: 'Sair do Sistema',
                icon: 'pi pi-sign-out',
                acceptLabel: 'Sair',
                acceptClass: 'bg-green-300 text-white',
                rejectLabel: 'Cancelar',
                rejectClass: 'bg-red-400 text-white',
                accept: () => {
                    Auth.logout()
                        .then(() => {
                            Token.remove();
                            this.$toast.add({ severity: 'warn', summary: 'Até logo!', detail: `Você está saindo do sistema.`, life: 3000 });
                            this.$router.push('/login');
                        })
                        .catch(error => {
                            if(error.response) {
                                if(error.response.data.msg) {
                                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                                    return;
                                }
                            }
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                        });
                },
            });
        }
    }
};
</script>

<style>
    .main-menu .p-tree-root * {
        font-size: 0.9rem;
    }

    .main-menu a {
        text-decoration: none;
        color: var(--p-tree-node-color);
    }
    .router-link-active {
        font-weight: bold;
    }

    .main-menu .p-icon {
        width: 0.8rem !important;
        height: 0.8rem !important;
    }

    .main-menu .p-tree-node-toggle-button {
        width: 0.5rem;
        height: 0.5rem;
    }

    .main-menu .p-tree-node-content {
        padding-top: 0.9;
    }

    @media (max-width: 1023px) {
        #app-sidebar.app-sidebar {
            position: fixed;
            width: min(86vw, 320px) !important;
            height: 100dvh !important;
            overflow-y: auto;
            z-index: 1202 !important;
            box-shadow: 0 0 0 9999px transparent;
        }

        .mobile-sidebar-backdrop {
            position: fixed;
            inset: 0;
            z-index: 1201;
            background: rgba(15, 23, 42, 0.45);
        }

        /* garante que conteúdo principal fique abaixo do menu mobile */
        .min-h-screen.flex.flex-column.relative.flex-auto {
            z-index: 1;
            min-width: 0;
        }

        .app-topbar {
            padding-left: 0.75rem !important;
            padding-right: 0.75rem !important;
        }

        .app-topbar .pi-bars {
            font-size: 1.75rem !important;
        }

        .app-content {
            padding: 0.5rem !important;
        }

        .app-footer {
            text-align: center;
            line-height: 1.35;
        }

        .app-logo {
            width: 140px;
            max-width: 100%;
            height: auto;
        }

        .mobile-top-actions {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }

        .mobile-theme-btn.p-button {
            background: transparent !important;
            border: 1px solid rgba(255, 255, 255, 0.75) !important;
            color: #ffffff !important;
            padding: 0.3rem 0.45rem !important;
        }

        .mobile-theme-btn .p-button-icon {
            color: #ffffff !important;
        }
    }

    .darkmode-toggle-btn.p-button {
        background: transparent !important;
        border: 1px solid rgba(255, 255, 255, 0.8) !important;
        color: #ffffff !important;
    }

    .darkmode-toggle-btn.p-button:hover {
        background: rgba(255, 255, 255, 0.2) !important;
        border-color: #ffffff !important;
    }

    .darkmode-toggle-btn .p-button-icon,
    .darkmode-toggle-btn .p-button-label {
        color: #ffffff !important;
    }
</style>
